import React from 'react';

import CardDialog from '~/components/CardDialog';
import LinkShare from '~/components/communications/LinkShare';
import { contactPopulate } from '~/components/Contacts/ContactUtils';
import Button from '~/components/core/Atomic/Buttons/Button';
import LoadingSwitch from '~/components/core/Loading/LoadingSwitch';
import Text from '~/components/core/TextComponents/Text';
import useOrganization from '~/components/OrganizationContext';
import useDataFetcher from '~/components/useDataFetcher';
import { useFetchClaim } from '~/hooks/useFetchClaim';
import type { ContactFullModel } from '~/server_shared/swagger/data-contracts';

import { buildSignatureRequestTemplateData } from './utils';

interface Props {
  contact: ContactFullModel;
  signUrl: string;
  claimId: number;
  onClose: () => void;
}

const PostSignatureRequestCard: React.FC<Props> = ({ contact, signUrl, claimId, onClose }) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { organizationContactRolesDict } = useOrganization();
  // Resolved from the claimId prop the way the sibling SmsCommunicationPopover resolves it, and
  // reused straight from claim context when this dialog is opened inside the claim page.
  const [claim, isLoadingClaim] = useFetchClaim(claimId);

  // The SMS body gets the organization's SMS signature, the same one SmsCommunicationCard prefills
  // with -- the email signature is HTML and LinkShare's text conversion would blow it past Twilio's
  // body limit (see the builder in ./utils).
  const { data: smsSignatureData, isError: isSmsSignatureError } = useDataFetcher<{
    user_sms_signature: string;
  }>(claim ? `/api/v1/organizations/${claim.organization_id}/sms_signature` : null, null, {
    // The degrade below is only silent if the failure does not also put a modal in front of the
    // dialog the user opened: useDataFetcher reports through reportAxiosError, whose default opens
    // the top-level error dialog. The /api/v1/errors report is unaffected and stands as the record --
    // the flag also gates Sentry.captureException, so it is the server-side report that survives.
    shouldOpenErrorDialog: false,
  });

  const smsSignature = smsSignatureData?.user_sms_signature;

  // The signature is needed only by the SMS action, so a failed fetch must not take copy-link and
  // email down with it: it degrades to no signature, which is what the route already answers for an
  // organization that has no SMS template. Waiting for it to settle, though, keeps the SMS body
  // from being seeded unsigned.
  const isSmsSignatureSettled = !claim || smsSignatureData !== undefined || isSmsSignatureError;

  const templateData = buildSignatureRequestTemplateData({ signUrl, smsSignature });

  // A claim's contacts carry no primary_email, and the email composer sends to it: contactPopulate
  // derives it from the contact's emails.
  const recipient = React.useMemo(() => (contact ? contactPopulate(contact) : contact), [contact]);

  return (
    <CardDialog isDialog title="Signing Link" fullWidth maxWidth="sm" onClose={onClose}>
      <LoadingSwitch isLoading={isLoadingClaim || !isSmsSignatureSettled}>
        <div className="flex flex-col space-y-20">
          <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
            A link was created for a new signature request. You can copy this link or send it to the contact.
          </Text>

          {contact ? (
            <div className="flex items-center gap-8">
              <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
                To:
              </Text>
              <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD}>
                {contact.full_name || '-'}
              </Text>
              {contact.role && organizationContactRolesDict?.[contact.role] ? (
                <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR} className="text-slate-600">
                  ({organizationContactRolesDict[contact.role].desc})
                </Text>
              ) : null}
            </div>
          ) : null}

          <LinkShare
            title="Signature Link"
            href={signUrl}
            contact={recipient}
            templateData={templateData}
            claimId={claimId}
          />
        </div>

        <div className="border-gray-200 mt-20 flex justify-end space-x-12 border-t p-20">
          <Button variant="contained" color="primary" onClick={onClose}>
            Done
          </Button>
        </div>
      </LoadingSwitch>
    </CardDialog>
  );
};

export default PostSignatureRequestCard;
